import React, { useState, useMemo, useRef, useEffect, useCallback, Fragment } from 'react';
import { motion } from 'framer-motion';
import { Listbox, Combobox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { 
  ChevronDown, 
  Check, 
  Search, 
  X, 
  Code,
  Layers,
  ArrowRight,
  Plus,
  Wand2,
  Key,
  Hash,
  Clock,
  Fingerprint,
  Shuffle
} from 'lucide-react';
import { ContractProperty, TransformationType, TransformationConfig } from '../../types/contract';
import { EventSchema } from '../../types/schema';

// Define transformation types and options
export const transformationTypes: { value: TransformationType; label: string; icon: React.ReactNode }[] = [
  { value: 'mapping', label: 'Mapping', icon: <ArrowRight className="h-4 w-4" /> },
  { value: 'enrichment', label: 'Enrichment', icon: <Wand2 className="h-4 w-4" /> },
  { value: 'lua', label: 'LUA Expression', icon: <Code className="h-4 w-4" /> },
  { value: 'spreading', label: 'Spreading', icon: <Layers className="h-4 w-4" /> }
];

export const enrichmentOptions = [
  { value: 'direct', label: 'Direct Value', icon: <ArrowRight className="h-4 w-4" /> },
  { value: 'uuid', label: 'UUID', icon: <Fingerprint className="h-4 w-4" /> },
  { value: 'timestamp', label: 'Timestamp', icon: <Clock className="h-4 w-4" /> },
  { value: 'random', label: 'Random Number', icon: <Hash className="h-4 w-4" /> },
  { value: 'hash', label: 'Hash Value', icon: <Key className="h-4 w-4" /> }
];

export const mappingOptions = [
  { value: 'direct', label: 'Direct Assign', icon: <ArrowRight className="h-4 w-4" /> },
  { value: 'regex', label: 'Regex Match', icon: <Search className="h-4 w-4" /> },
  { value: 'join', label: 'Join Values', icon: <Plus className="h-4 w-4" /> },
  { value: 'transform', label: 'Transform', icon: <Wand2 className="h-4 w-4" /> },
  { value: 'conditional', label: 'Conditional', icon: <Shuffle className="h-4 w-4" /> }
];

interface ContractPropertyEditFormProps {
  property: ContractProperty;
  normalizedProperty: ContractProperty;
  availableMappings: { name: string; path: string; type: string; certaintyScore?: number }[];
  handleTransformationConfigUpdate: (propertyId: string, configUpdates: Partial<TransformationConfig>) => void;
  handleTransformationTypeChange: (propertyId: string, type: TransformationType) => void;
  handleUpdateProperty: (propertyId: string, updates: Partial<ContractProperty>) => void;
  handlePropertyMappingUpdate: (
    propertyId: string,
    normalizedPath: string,
    sourceMatchType?: 'manual' | 'ai',
    aiCertaintyScore?: number,
    aiSuggested?: boolean
  ) => void;
  onClose: () => void;
  // New props for event associations
  allEvents: EventSchema[];
  handlePropertyEventAssociationChange: (propertyId: string, eventId: string, isAssociated: boolean) => void;
}

const ContractPropertyEditForm: React.FC<ContractPropertyEditFormProps> = ({
  property,
  normalizedProperty,
  availableMappings,
  handleTransformationConfigUpdate,
  handleTransformationTypeChange,
  handleUpdateProperty,
  handlePropertyMappingUpdate,
  onClose,
  allEvents,
  handlePropertyEventAssociationChange,
}) => {
  const editFormRef = useRef<HTMLDivElement>(null);
  const [localTransformationType, setLocalTransformationType] = useState(normalizedProperty?.transformationType || 'mapping');
  const [localTransformationConfig, setLocalTransformationConfig] = useState<TransformationConfig>(normalizedProperty?.transformationConfig || { mappingType: 'direct' });
  const [query, setQuery] = useState('');
  const [localName, setLocalName] = useState(property.name);
  const [localCodeName, setLocalCodeName] = useState(property.codeName);
  const [localDataType, setLocalDataType] = useState(property.dataType);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (property.isNew) {
      setLocalName('');
      setLocalCodeName('');
      setLocalDataType('string');
    } else {
      setLocalName(property.name);
      setLocalCodeName(property.codeName);
      setLocalDataType(property.dataType);
    }
  }, [property.isNew, property.name, property.codeName, property.dataType]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editFormRef.current && !editFormRef.current.contains(event.target as Node)) {
        if (hasUnsavedChanges) {
          // Changes are already propagated to parent state, so just reset the flag and close.
          setHasUnsavedChanges(false); // Reset unsaved changes flag
        }
        onClose(); // Always close the form
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, hasUnsavedChanges]);

  const handleCustomPropertyChange = (field: 'name' | 'codeName' | 'dataType', value: string) => {
    if (field === 'name') setLocalName(value);
    if (field === 'codeName') setLocalCodeName(value);
    if (field === 'dataType') setLocalDataType(value as ContractProperty['dataType']);
    
    handleUpdateProperty(property.id, { [field]: value });
    setHasUnsavedChanges(true);
  };

  const handleLocalTransformationTypeChange = (type: string) => {
    const newType = type as TransformationType;
    setLocalTransformationType(newType);
    
    let defaultConfig: TransformationConfig = {};
    switch (newType) {
      case 'mapping':
        defaultConfig = { mappingType: 'direct' };
        break;
      case 'enrichment':
        defaultConfig = { enrichmentType: 'direct' }; // Default to 'direct' for enrichment
        break;
      case 'lua':
        defaultConfig = { script: 'function transform(input)\n  -- Your transformation logic here\n  return input\nend' };
        break;
      case 'spreading':
        defaultConfig = {};
        break;
    }
    setLocalTransformationConfig(defaultConfig);
    handleTransformationTypeChange(property.id, newType);
    handleTransformationConfigUpdate(property.id, defaultConfig);
    setHasUnsavedChanges(true);
  };

  const handleConfigChange = (field: keyof TransformationConfig, value: any) => {
    const newConfig = { ...localTransformationConfig, [field]: value };
    setLocalTransformationConfig(newConfig);
    handleTransformationConfigUpdate(property.id, { [field]: value });
    setHasUnsavedChanges(true);
  };

  const handleMappingSelect = (path: string) => {
    handlePropertyMappingUpdate(property.id, path, 'manual');
    setHasUnsavedChanges(true);
  };

  const filteredMappings = useMemo(() => {
    return query === ''
      ? availableMappings
      : availableMappings.filter((mapping) =>
          mapping.path.toLowerCase().includes(query.toLowerCase())
        );
  }, [query, availableMappings]);

  // Get current event associations based on property.eventAssociations
  const currentEventAssociations = useMemo(() => {
    const associatedEventIds = new Set(property.eventAssociations || []);
    return (allEvents || []).filter(event =>
      associatedEventIds.has(event.id)
    );
  }, [allEvents, property.eventAssociations]);

  // Handle event selection changes
  const handleEventSelectChange = useCallback((selectedEvents: EventSchema[]) => {
    const newSelectedEventIds = selectedEvents.map(event => event.id);
    const oldSelectedEventIds = currentEventAssociations.map(event => event.id);

    // Find added and removed events
    const addedEventIds = newSelectedEventIds.filter(id => !oldSelectedEventIds.includes(id));
    const removedEventIds = oldSelectedEventIds.filter(id => !newSelectedEventIds.includes(id));

    // Update associations
    addedEventIds.forEach(eventId => {
      handlePropertyEventAssociationChange(property.id, eventId, true);
    });

    removedEventIds.forEach(eventId => {
      handlePropertyEventAssociationChange(property.id, eventId, false);
    });
    
    setHasUnsavedChanges(true);
  }, [property.id, currentEventAssociations, handlePropertyEventAssociationChange]);

  return (
    <motion.div
      ref={editFormRef}
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2 }}
      className="w-full bg-blue-50 dark:bg-gray-900/50 p-6"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      }}
    >
      {property.isNew && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
            <input type="text" value={localName} onChange={(e) => handleCustomPropertyChange('name', e.target.value)} className="w-full border-gray-300 dark:border-gray-700 rounded-md shadow-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code Name</label>
            <input type="text" value={localCodeName} onChange={(e) => handleCustomPropertyChange('codeName', e.target.value)} className="w-full border-gray-300 dark:border-gray-700 rounded-md shadow-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data Type</label>
            <select value={localDataType} onChange={(e) => handleCustomPropertyChange('dataType', e.target.value)} className="w-full border-gray-300 dark:border-gray-700 rounded-md shadow-sm">
              <option value="string">string</option>
              <option value="number">number</option>
              <option value="boolean">boolean</option>
              <option value="object">object</option>
              <option value="array">array</option>
            </select>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Transformation Type
          </label>
          <Listbox value={localTransformationType} onChange={handleLocalTransformationTypeChange}>
            <div className="relative">
              <Listbox.Button className="relative w-full cursor-default rounded-md bg-white dark:bg-gray-800 py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm">
                <span className="block truncate">{transformationTypes.find(t => t.value === localTransformationType)?.label}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </Listbox.Button>
              <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-900 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {transformationTypes.map((type) => (
                  <Listbox.Option key={type.value} value={type.value} className={({ active }) => `relative cursor-default select-none py-2 pl-10 pr-4 ${ active ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-gray-200' }`}>
                    {({ selected }) => (
                      <>
                        <span className={`block truncate ${ selected ? 'font-medium' : 'font-normal' }`}>{type.label}</span>
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                            <Check className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </div>
          </Listbox>
        </div>

        {['mapping', 'spreading'].includes(localTransformationType) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Source Raw Property
            </label>
            <div className="relative">
              <Combobox
                value={normalizedProperty?.normalizedPath || ''}
                onChange={handleMappingSelect}
                disabled={localTransformationType !== 'mapping'}
              >
                <div className="relative">
                  <Combobox.Input
                    className="w-full border-gray-300 dark:border-gray-700 rounded-md shadow-sm py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 dark:text-gray-200 bg-white dark:bg-gray-800 focus:ring-0 disabled:bg-gray-100 dark:disabled:bg-gray-800"
                    onChange={(event) => setQuery(event.target.value)}
                    displayValue={(path: string) => path}
                  />
                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </Combobox.Button>
                </div>
                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-900 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {filteredMappings.map((mapping) => (
                    <Combobox.Option key={mapping.path} value={mapping.path} className={({ active }) => `relative cursor-default select-none py-2 pl-10 pr-4 ${ active ? 'bg-blue-600 text-white' : 'text-gray-900 dark:text-gray-200' }`}>
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${ selected ? 'font-medium' : 'font-normal' }`}>{mapping.path}</span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                              <Check className="h-5 w-5" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              </Combobox>
              {normalizedProperty?.normalizedPath && (
                <button
                  type="button"
                  onClick={() => handleMappingSelect('')}
                  className="absolute inset-y-0 right-8 flex items-center"
                  title="Clear selection"
                  disabled={localTransformationType !== 'mapping'}
                >
                  <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
          </div>
        )}

        {localTransformationType === 'lua' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              LUA Script
            </label>
            <textarea
              value={localTransformationConfig.script || ''}
              onChange={(e) => handleConfigChange('script', e.target.value)}
              rows={6}
              className="w-full p-2 border font-mono text-sm bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="function transform(input)&#10;  return input&#10;end"
            />
            <p className="text-xs text-gray-500 mt-1">The script should be a LUA function that accepts 'input' and returns the transformed value.</p>
          </div>
        )}

        {localTransformationType === 'enrichment' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Enrichment Type
            </label>
            <Listbox value={localTransformationConfig.enrichmentType || 'direct'} onChange={(value) => handleConfigChange('enrichmentType', value)}>
              <div className="relative">
                <Listbox.Button className="relative w-full cursor-default rounded-md bg-white dark:bg-gray-800 py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm">
                  <span className="block truncate">{enrichmentOptions.find(o => o.value === localTransformationConfig.enrichmentType)?.label || 'Select an option'}</span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </span>
                </Listbox.Button>
                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-900 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {enrichmentOptions.map((option) => (
                    <Listbox.Option key={option.value} value={option.value} className={({ active }) => `relative cursor-default select-none py-2 pl-10 pr-4 ${ active ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-gray-200' }`}>
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${ selected ? 'font-medium' : 'font-normal' }`}>{option.label}</span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                              <Check className="h-5 w-5" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            </Listbox>

            {localTransformationConfig.enrichmentType === 'direct' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Direct Value
                </label>
                <input
                  type="text"
                  value={localTransformationConfig.value || ''}
                  onChange={(e) => handleConfigChange('value', e.target.value)}
                  className="w-full border-gray-300 dark:border-gray-700 rounded-md shadow-sm"
                  placeholder="Enter static value"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Associated Events Section */}
      <div className="mt-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Associated Events
        </label>
        <div className="flex flex-row gap-2 items-end w-full">
          <Listbox
            value={currentEventAssociations}
            onChange={handleEventSelectChange as (value: EventSchema[]) => void}
            multiple
          >
            {({ open }) => (
              <div className="relative flex-1">
                <ListboxButton
                  className="relative w-full cursor-default rounded border py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-1 sm:text-sm
                    border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring-blue-500/30 dark:focus:ring-blue-500/20
                    bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <div className="flex flex-wrap gap-1 pr-8">
                    {currentEventAssociations.length > 0 ? (
                      currentEventAssociations.map(event => (
                        <span
                          key={event.id}
                          className="inline-flex items-center rounded-full bg-blue-200 px-2.5 py-0.5 text-xs font-medium text-blue-900 dark:bg-blue-700 dark:text-blue-100"
                        >
                          {event.name}
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent Listbox from opening
                              handlePropertyEventAssociationChange(property.id, event.id, false);
                              setHasUnsavedChanges(true);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                handlePropertyEventAssociationChange(property.id, event.id, false);
                                setHasUnsavedChanges(true);
                              }
                            }}
                            className="ml-1 -mr-0.5 h-4 w-4 flex-shrink-0 rounded-full hover:bg-blue-300 dark:hover:bg-blue-600 inline-flex items-center justify-center text-blue-900 dark:text-blue-100"
                          >
                            <span className="sr-only">Remove {event.name}</span>
                            <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                            </svg>
                          </span>
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">Select events...</span>
                    )}
                  </div>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                </ListboxButton>

                <Transition
                  show={open}
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions
                    className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm
                      dark:bg-gray-800 dark:border dark:border-gray-700"
                  >
                    {(allEvents || []).map((event) => (
                      <ListboxOption
                        key={event.id}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${
                            active ? 'text-white bg-blue-600' : 'text-gray-900 dark:text-gray-100'
                          }`
                        }
                        value={event}
                      >
                        {({ selected, active }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                              {event.name}
                            </span>
                            {selected ? (
                              <span
                                className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                  active ? 'text-white' : 'text-blue-600'
                                }`}
                              >
                                <Check className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Transition>
              </div>
            )}
          </Listbox>
          <button
            type="button"
            onClick={() => {
              allEvents.forEach(event => {
                if (!currentEventAssociations.some(assocEvent => assocEvent.id === event.id)) {
                  handlePropertyEventAssociationChange(property.id, event.id, true);
                }
              });
              setHasUnsavedChanges(true);
            }}
            disabled={currentEventAssociations.length === (allEvents || []).length}
            className={`flex items-center gap-2 px-4 py-2.5 rounded text-sm font-medium transition-colors
              bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600
              ${currentEventAssociations.length === (allEvents || []).length ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            Assign All
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ContractPropertyEditForm;
